import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardBody, Button, Spinner, Alert, Input, Badge, Modal } from '@components/ui';
import { DocumentTextIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useInvoicesArchive } from '../../invoices/hooks/useInvoices';
import { useSelectedCompany } from '../../company/hooks/useSelectedCompany';
import { useAuthStore } from '@features/auth/store/authStore';
import { getHomeRouteForRole } from '@lib/utils/roleNavigation';

// Mapeo de colores segun EInvoiceStatus (equivalente a los label-* de Bootstrap)
const getStatusVariant = (eInvoiceStatus: number): 'success' | 'danger' | 'warning' | 'default' => {
  switch (eInvoiceStatus) {
    case 0: return 'success';    // Procesado
    case 1: return 'danger';     // Error
    case 2: return 'warning';    // Autorizado uso de FE
    case 3: return 'warning';    // Procesando
    case 4: return 'warning';    // Pendiente
    case 5: return 'default';    // Anulada
    default: return 'default';
  }
};

// Mapeo de variante de Badge a color de texto para el icono de advertencia
const getIconColorClass = (variant: string): string => {
  switch (variant) {
    case 'info': return 'text-primary-600';
    case 'danger': return 'text-danger-600';
    case 'warning': return 'text-warning-600';
    default: return 'text-secondary-600';
  }
};

function StatusBadgeWithModal({
  status,
  eInvoiceStatus,
  responseCode,
  resolution,
  forceWaitOnEndpoints,
}: {
  status: string;
  eInvoiceStatus: number;
  responseCode?: string;
  resolution?: string;
  forceWaitOnEndpoints?: string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const variant = getStatusVariant(eInvoiceStatus);
  const tooltipText = [responseCode, resolution].filter(Boolean).join(' - ');
  const showWarningIcon = forceWaitOnEndpoints === 'true';
  const iconColor = getIconColorClass(variant);

  return (
    <>
      <span className="inline-flex items-center gap-1" title={tooltipText || undefined}>
        {showWarningIcon && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className={`${iconColor} hover:opacity-70 transition-opacity`}
            title="Ver detalle del estado"
          >
            <ExclamationCircleIcon className="w-4 h-4" />
          </button>
        )}
        <button type="button" onClick={() => setIsModalOpen(true)}>
          <Badge variant={variant} size="sm">
            {status || 'Desconocido'}
          </Badge>
        </button>
      </span>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Detalle del estado"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={variant} size="md">
              {status || 'Desconocido'}
            </Badge>
          </div>
          {responseCode && (
            <div className="rounded-lg bg-secondary-50 dark:bg-secondary-700 p-3">
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
                Código de respuesta
              </p>
              <p className="mt-1 text-sm text-secondary-900 dark:text-white">
                {responseCode}
              </p>
            </div>
          )}
          {resolution && (
            <div className="rounded-lg bg-secondary-50 dark:bg-secondary-700 p-3">
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
                Resolución
              </p>
              <p className="mt-1 text-sm text-secondary-900 dark:text-white">
                {resolution}
              </p>
            </div>
          )}
          {!responseCode && !resolution && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              No hay información adicional disponible.
            </p>
          )}
        </div>
        <div className="mt-6 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setIsModalOpen(false)}>
            Cerrar
          </Button>
        </div>
      </Modal>
    </>
  );
}



// Mapeo de tipos de documento
const docTypeLabels: Record<string, { text: string; color: string }> = {
  '01': { text: 'FA', color: 'bg-secondary-700 text-white' },
  '02': { text: 'FI', color: 'bg-primary-500 text-white' },
  '03': { text: 'FEXP', color: 'bg-info-500 text-white' },
  '04': { text: 'NC', color: 'bg-success-500 text-white' },
  '05': { text: 'ND', color: 'bg-warning-500 text-white' },
  '06': { text: 'NCG', color: 'bg-primary-500 text-white' },
  '07': { text: 'NDG', color: 'bg-primary-500 text-white' },
  '08': { text: 'FZF', color: 'bg-primary-500 text-white' },
  '09': { text: 'REMB', color: 'bg-info-500 text-white' },
  '10': { text: 'FOPE', color: 'bg-secondary-700 text-white' },
};

export function MinorPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const homeRoute = getHomeRouteForRole(user?.role);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Default date range: last 6 months. Constrains the Aludra query window so
  // high-volume companies don't timeout fetching all history since 2024-01-01.
  const getDefaultStartDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  };
  const getDefaultEndDate = () => new Date().toISOString().split('T')[0];

  // Estados para los valores de los inputs (filtros locales)
  const [storeCode, setStoreCode] = useState('');
  const [billingPoint, setBillingPoint] = useState('');
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [cufe, setCufe] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [customerNumberId, setCustomerNumberId] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [documentType, setDocumentType] = useState('');

  // Estados para los filtros aplicados (se envían al backend al buscar)
  const [appliedFilters, setAppliedFilters] = useState({
    storeCode: '',
    billingPoint: '',
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    cufe: '',
    customerName: '',
    documentNumber: '',
    customerNumberId: '',
    invoiceStatus: '',
    documentType: '',
  });

  // Get selected company
  const { data: selectedCompanyData } = useSelectedCompany();

  // Extract companyId to use in the query key
  const companyId = (selectedCompanyData as any)?.company?.id || selectedCompanyData?.id;

  // Backend-side filters: passes initDate/endDate y CUFE a Aludra. CUFE va por
  // backend porque algunos registros del archive devuelven cufe vacío y el filtro
  // client-side fallaría; Aludra ya filtra server-side cuando el payload incluye Cufe.
  // Aludra almacena el CUFE SIN el prefijo "FE", así que lo removemos antes de enviarlo.
  const normalizedCufe = appliedFilters.cufe?.trim().replace(/^FE/i, '') || undefined;
  const { data: allInvoicesData, isLoading, error, refetch } = useInvoicesArchive({
    page,
    pageSize,
    companyId,
    initDate: appliedFilters.startDate || undefined,
    endDate: appliedFilters.endDate || undefined,
    cufe: normalizedCufe,
    customerName: appliedFilters.customerName || undefined,
    documentNumber: appliedFilters.documentNumber || undefined,
    customerNumberId: appliedFilters.customerNumberId || undefined,
    invoiceStatus: appliedFilters.invoiceStatus || undefined,
    documentType: appliedFilters.documentType || undefined,
    storeCode: appliedFilters.storeCode || undefined,
    billingPoint: appliedFilters.billingPoint || undefined,
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [appliedFilters]);

  // Paginación y filtrado 100% server-side (electronicInvoiceSearchOnLite):
  // el backend ya filtra y pagina; usamos las filas y el total que devuelve.
  // Antes se traían hasta 5000 facturas (con XML completo) y se filtraba/paginaba
  // en el cliente — pesado para Aludra y lento. Verificado: Lite devuelve el mismo
  // dataset, TotalRecords confiable y filtra server-side (incl. documentNumber).
  const displayedInvoices = allInvoicesData?.invoices ?? [];
  const total = allInvoicesData?.total ?? 0;

  // Debug: log selected company and archive invoice data
  console.log('🏢 MinorPage - Selected company:', {
    id: (selectedCompanyData as any)?.company?.id || selectedCompanyData?.id,
    name: (selectedCompanyData as any)?.company?.name || (selectedCompanyData as any)?.name,
    code: (selectedCompanyData as any)?.company?.code || (selectedCompanyData as any)?.code
  });
  console.log('📋 MinorPage - Archive invoice data:', {
    count: allInvoicesData?.invoices?.length,
    total: allInvoicesData?.total,
  });

  const handleSearch = () => {
    // Apply current filter values
    setAppliedFilters({
      storeCode,
      billingPoint,
      startDate,
      endDate,
      cufe,
      customerName,
      documentNumber,
      customerNumberId,
      invoiceStatus,
      documentType,
    });
  };

  const handleClearFilters = () => {
    const defaultStart = getDefaultStartDate();
    const defaultEnd = getDefaultEndDate();
    setStoreCode('');
    setBillingPoint('');
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setCufe('');
    setCustomerName('');
    setDocumentNumber('');
    setCustomerNumberId('');
    setInvoiceStatus('');
    setDocumentType('');
    setAppliedFilters({
      storeCode: '',
      billingPoint: '',
      startDate: defaultStart,
      endDate: defaultEnd,
      cufe: '',
      customerName: '',
      documentNumber: '',
      customerNumberId: '',
      invoiceStatus: '',
      documentType: '',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    const err = error as any;
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.message || 'Error desconocido';
    return (
      <Alert variant="danger" title="Error al cargar facturas">
        <p>
          {status ? `[${status}] ` : ''}
          {message}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
          Reintentar
        </Button>
      </Alert>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Histórico
        </h1>
        <nav className="mt-2 flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link
                to={homeRoute}
                className="text-secondary-500 hover:text-secondary-700 dark:text-secondary-400"
              >
                Inicio
              </Link>
            </li>
            <li className="flex items-center">
              <span className="mx-2 text-secondary-400">/</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                Histórico
              </span>
            </li>
          </ol>
        </nav>
      </div>

      {/* Warning Alert */}
      <Alert variant="warning" title="">
        En esta pantalla se muestran los registros mayores a 90 días.
      </Alert>

      {/* Search Filters */}
      <Card>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                CUFE
              </label>
              <Input
                type="text"
                placeholder="Buscar por CUFE..."
                value={cufe}
                onChange={(e) => setCufe(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Nombre del Cliente
              </label>
              <Input
                type="text"
                placeholder="Buscar por nombre..."
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Número de Documento
              </label>
              <Input
                type="text"
                placeholder="Buscar por número..."
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                RUC del Cliente
              </label>
              <Input
                type="text"
                placeholder="Buscar por RUC..."
                value={customerNumberId}
                onChange={(e) => setCustomerNumberId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Estado de la Factura
              </label>
              <select
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                value={invoiceStatus}
                onChange={(e) => setInvoiceStatus(e.target.value)}
              >
                <option value="">Todos los estados</option>
                <option value="0">Procesada</option>
                <option value="1">Error</option>
                <option value="2">Autorizado uso de la FE</option>
                <option value="3">Procesando</option>
                <option value="4">Pendiente</option>
                <option value="5">Anulada</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Tipo de Documento
              </label>
              <select
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
              >
                <option value="">Todos los tipos</option>
                <option value="01">Factura de Operación Interna</option>
                <option value="02">Factura de Importación</option>
                <option value="03">Factura de Exportación</option>
                <option value="04">Nota de Crédito Referente a una o Varias FE</option>
                <option value="05">Nota de Débito Referente a una o Varias FE</option>
                <option value="06">Nota de Crédito Genérica</option>
                <option value="07">Nota de Débito Genérica</option>
                <option value="08">Factura de Zona Franca</option>
                <option value="09">Reembolso</option>
                <option value="10">Factura de operación extranjera</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Código de tienda
              </label>
              <Input
                type="text"
                placeholder="Código de tienda..."
                value={storeCode}
                onChange={(e) => setStoreCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Punto de venta
              </label>
              <Input
                type="text"
                placeholder="Punto de venta..."
                value={billingPoint}
                onChange={(e) => setBillingPoint(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Fecha inicio
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Fecha fin
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button
              variant="primary"
              onClick={handleSearch}
              className="w-full md:w-auto"
            >
              Buscar
            </Button>
            <Button
              variant="outline"
              onClick={handleClearFilters}
              className="w-full md:w-auto"
            >
              Limpiar filtros
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardBody className="p-0">
          <div className="bg-secondary-50 dark:bg-secondary-800 px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-secondary-700 dark:text-secondary-300" />
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
                  Tabla de contenido
                </h3>
              </div>
              <span className="text-sm text-secondary-500 dark:text-secondary-400">
                {total} registro{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {displayedInvoices.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
                No se han encontrado registros
              </h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                <thead className="bg-secondary-50 dark:bg-secondary-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      # de Documento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Tipo de Documento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      RUC
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                  {displayedInvoices.map((invoice) => {
                    const docType = invoice.documentType ? docTypeLabels[invoice.documentType] || { text: invoice.documentType ?? '', color: 'bg-secondary-500 text-white' } : { text: '', color: 'bg-secondary-500 text-white' };
                    return (
                      <tr
                        key={invoice.id}
                        className="hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900 dark:text-white">
                          <Link
                            to={`/pages/invoice/${invoice.id}`}
                            className="text-primary-600 dark:text-primary-400 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {invoice.documentNumber}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${docType.color}`}>
                            {docType.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white">
                          {invoice.customerName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                          {invoice.customerNumberId || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-secondary-900 dark:text-white">
                          ${invoice.amountWithTax?.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <small className="text-secondary-500 dark:text-secondary-400">
                              {(() => {
                                const d = invoice.dateIssue ? new Date(invoice.dateIssue) : null;
                                return d && !isNaN(d.getTime()) ? format(d, 'dd/MM/yyyy HH:mm') : '-';
                              })()}
                            </small>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadgeWithModal
                            status={invoice.status ?? ''}
                            eInvoiceStatus={invoice.eInvoiceStatus ?? 0}
                            responseCode={invoice.responseCode}
                            resolution={invoice.resolution}
                            forceWaitOnEndpoints={invoice.forceWaitOnEndpoints}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/pages/invoice/${invoice.id}`, { state: { from: '/home/minor' } });
                            }}
                            className="inline-flex items-center gap-1"
                          >
                            <EyeIcon className="h-4 w-4" />
                            Ver
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={8} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-secondary-500 dark:text-secondary-400">
                          Mostrando {((page - 1) * pageSize) + 1} a {Math.min(page * pageSize, total)} de {total}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </Button>
                          <span className="text-sm text-secondary-700 dark:text-secondary-300">
                            Página {page} de {totalPages || 1}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
